import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  db,
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
  getDocs,
  Timestamp,
} from '../firebase';
import { todoAssigneeIds } from '../lib/todoAssignees';
import { HAYDI_ITEM_COLORS, DEFAULT_ITEM_COLOR, normalizeItemColor } from '../lib/itemColors';
import {
  buildTodoReminderFields,
  REMINDER_OFFSET_PRESETS,
  formatTodoReminderDisplay,
} from '../lib/todoReminders';

function priorityClass(p) {
  if (p === 'high') return 'haydi-chip haydi-chip--high';
  if (p === 'med') return 'haydi-chip haydi-chip--med';
  if (p === 'low') return 'haydi-chip haydi-chip--low';
  return null;
}

function ColorSwatches({ value, onChange, ariaLabel }) {
  return (
    <div className="haydi-color-swatches" role="group" aria-label={ariaLabel}>
      {HAYDI_ITEM_COLORS.map((c) => (
        <button
          key={c.id}
          type="button"
          className={`haydi-color-swatch${value === c.hex ? ' haydi-color-swatch--active' : ''}`}
          style={{ '--swatch': c.hex }}
          onClick={() => onChange(c.hex)}
          aria-pressed={value === c.hex}
          title={c.id}
        />
      ))}
    </div>
  );
}

function todoDeadlineState(todo) {
  if (!todo.deadlineAt?.toDate || todo.done) return null;
  const d = todo.deadlineAt.toDate();
  const now = new Date();
  if (d < now) return 'overdue';
  const day = 86400000;
  if (d.getTime() - now.getTime() < day) return 'soon';
  return 'ok';
}

function isDeadlinePassed(todo) {
  if (!todo.deadlineAt?.toDate) return false;
  return todo.deadlineAt.toDate().getTime() < Date.now();
}

function sanitizePoints(raw) {
  if (raw === '' || raw === null || raw === undefined) return 0;
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1_000_000, Math.trunc(n)));
}

function TodoTab({ user }) {
  const { t, i18n } = useTranslation();
  const [personal, setPersonal] = useState([]);
  const [newTodo, setNewTodo] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [assignedUserIds, setAssignedUserIds] = useState([]);
  const [priority, setPriority] = useState('none');
  const [color, setColor] = useState(DEFAULT_ITEM_COLOR);
  const [deadlineLocal, setDeadlineLocal] = useState('');
  const [notifyLocal, setNotifyLocal] = useState('');
  const [reminderKind, setReminderKind] = useState('none');
  const [beforeOffsetSelect, setBeforeOffsetSelect] = useState('15');
  const [beforeOffsetCustom, setBeforeOffsetCustom] = useState('');
  const [points, setPoints] = useState('');
  const [pointsDrafts, setPointsDrafts] = useState({});
  const [pointsSaving, setPointsSaving] = useState({});
  const [users, setUsers] = useState([]);
  const uid = user?.uid;
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!uid) return;
    const q1 = query(
      collection(db, 'todos'),
      where('userId', '==', uid),
      where('isGlobal', '==', false),
    );
    const u1 = onSnapshot(q1, (snap) => {
      const rows = snap.docs.map((d) => ({ _id: d.id, ...d.data() }));
      rows.sort((a, b) => {
        const ad = a.done ? 1 : 0;
        const bd = b.done ? 1 : 0;
        if (ad !== bd) return ad - bd;
        const da = a.deadlineAt?.toDate?.()?.getTime() ?? Number.POSITIVE_INFINITY;
        const db = b.deadlineAt?.toDate?.()?.getTime() ?? Number.POSITIVE_INFINITY;
        if (da !== db) return da - db;
        return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
      });
      setPersonal(rows);
    });
    (async () => {
      const us = await getDocs(collection(db, 'users'));
      setUsers(us.docs.map((d) => ({ _id: d.id, ...d.data() })));
    })();
    return () => {
      u1();
    };
  }, [uid]);

  const handleAddTodo = async () => {
    if (!newTodo.trim() || !uid) return;
    const desc = newDescription.trim();
    const assignees = assignedUserIds.filter(Boolean);
    const deadlineAt = deadlineLocal.trim() ? Timestamp.fromDate(new Date(deadlineLocal)) : null;

    let beforeM = null;
    if (reminderKind === 'before') {
      beforeM =
        beforeOffsetSelect === 'custom'
          ? parseInt(String(beforeOffsetCustom).trim(), 10)
          : parseInt(beforeOffsetSelect, 10);
      if (!Number.isFinite(beforeM) || beforeM < 0) {
        window.alert(t('todoReminderBeforeInvalid'));
        return;
      }
      if (!deadlineAt) {
        window.alert(t('todoReminderNeedsDeadline'));
        return;
      }
    }
    if (reminderKind === 'at' && !notifyLocal.trim()) {
      window.alert(t('todoReminderAtNeedsTime'));
      return;
    }

    const rf = buildTodoReminderFields({
      deadlineAt,
      reminderKind,
      notifyAtLocal: notifyLocal,
      beforeDeadlineMinutes: reminderKind === 'before' ? beforeM : null,
    });
    const hasReminder = !!rf.nextReminderAt;

    await addDoc(collection(db, 'todos'), {
      text: newTodo.trim(),
      description: desc || null,
      userId: uid,
      isGlobal: false,
      assignedUserIds: assignees.length ? assignees : null,
      done: false,
      lateDone: false,
      points: sanitizePoints(points),
      awardedPoints: 0,
      priority: priority === 'none' ? null : priority,
      color: normalizeItemColor(color),
      deadlineAt,
      notifyAt: rf.notifyAt,
      nextReminderAt: rf.nextReminderAt,
      reminderBeforeDeadlineMinutes: rf.reminderBeforeDeadlineMinutes,
      ...(hasReminder ? { reminderSent: false } : {}),
      ...(deadlineAt ? { deadlineNotifySent: false } : {}),
      createdAt: serverTimestamp(),
    });
    setNewTodo('');
    setNewDescription('');
    setAssignedUserIds([]);
    setPriority('none');
    setColor(DEFAULT_ITEM_COLOR);
    setDeadlineLocal('');
    setNotifyLocal('');
    setReminderKind('none');
    setBeforeOffsetSelect('15');
    setBeforeOffsetCustom('');
    setPoints('');
  };

  const toggleDone = async (todo) => {
    if (todo.done) {
      await updateDoc(doc(db, 'todos', todo._id), { done: false, lateDone: false });
    } else {
      await updateDoc(doc(db, 'todos', todo._id), { done: true, lateDone: false });
    }
  };

  const markLateDone = async (todo) => {
    await updateDoc(doc(db, 'todos', todo._id), { done: true, lateDone: true });
  };

  const saveTodoPoints = async (todoId) => {
    if (!isAdmin) return;
    const value = sanitizePoints(pointsDrafts[todoId]);
    setPointsSaving((s) => ({ ...s, [todoId]: true }));
    try {
      await updateDoc(doc(db, 'todos', todoId), { points: value });
      setPointsDrafts((d) => {
        const next = { ...d };
        delete next[todoId];
        return next;
      });
    } finally {
      setPointsSaving((s) => ({ ...s, [todoId]: false }));
    }
  };

  const removeTodo = async (id) => {
    if (!window.confirm(t('confirmDeleteItem'))) return;
    await deleteDoc(doc(db, 'todos', id));
  };

  const assigneeNames = (todo) => {
    const ids = todoAssigneeIds(todo);
    return ids
      .map((id) => users.find((u) => u._id === id)?.name)
      .filter(Boolean)
      .join(', ');
  };

  const formatTs = (ts) => (ts?.toDate ? ts.toDate().toLocaleString(i18n.language) : '');

  return (
    <div className="haydi-todo-page">
      <header className="haydi-todo-page__hero">
        <h1 className="haydi-todo-page__title">{t('todoListTitle')}</h1>
        <p className="haydi-todo-page__lead">{t('todoSubtitle')}</p>
      </header>

      <div className="haydi-todo-layout">
        <section className="haydi-pro-panel haydi-pro-panel--elevated">
          <div className="haydi-pro-panel__head">
            <h2>{t('myTodos')}</h2>
            <p className="haydi-pro-panel__meta">{t('myTodosHint')}</p>
          </div>
          <div className="haydi-pro-panel__body">
            <div className="haydi-todo-composer">
              <label className="haydi-muted haydi-label-xs">{t('itemColor')}</label>
              <ColorSwatches value={color} onChange={setColor} ariaLabel={t('itemColor')} />

              <label className="haydi-muted haydi-label-xs">{t('todoPlaceholder')}</label>
              <input
                type="text"
                className="haydi-input"
                placeholder={t('todoPlaceholder')}
                value={newTodo}
                onChange={(e) => setNewTodo(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleAddTodo();
                  }
                }}
              />

              <label className="haydi-muted haydi-label-xs">{t('todoDescription')}</label>
              <textarea
                className="haydi-input"
                placeholder={t('todoDescriptionPlaceholder')}
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                rows={4}
              />

              <div className="haydi-todo-composer__grid2">
                <div>
                  <label className="haydi-muted haydi-label-xs">{t('todoDeadline')}</label>
                  <input
                    type="datetime-local"
                    className="haydi-input"
                    value={deadlineLocal}
                    onChange={(e) => setDeadlineLocal(e.target.value)}
                  />
                </div>
                <div>
                  <label className="haydi-muted haydi-label-xs">{t('todoReminderKind')}</label>
                  <select
                    className="haydi-select haydi-input"
                    value={reminderKind}
                    onChange={(e) => setReminderKind(e.target.value)}
                  >
                    <option value="none">{t('todoReminderKindNone')}</option>
                    <option value="at">{t('todoReminderKindAt')}</option>
                    <option value="before">{t('todoReminderKindBefore')}</option>
                  </select>
                </div>
              </div>
              {reminderKind === 'at' && (
                <div>
                  <label className="haydi-muted haydi-label-xs">{t('todoNotifyAt')}</label>
                  <input
                    type="datetime-local"
                    className="haydi-input"
                    value={notifyLocal}
                    onChange={(e) => setNotifyLocal(e.target.value)}
                  />
                </div>
              )}
              {reminderKind === 'before' && (
                <div className="haydi-todo-composer__grid2">
                  <div>
                    <label className="haydi-muted haydi-label-xs">{t('todoReminderBeforeOffset')}</label>
                    <select
                      className="haydi-select haydi-input"
                      value={beforeOffsetSelect}
                      onChange={(e) => setBeforeOffsetSelect(e.target.value)}
                    >
                      {REMINDER_OFFSET_PRESETS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {t(o.labelKey)}
                        </option>
                      ))}
                    </select>
                  </div>
                  {beforeOffsetSelect === 'custom' && (
                    <div>
                      <label className="haydi-muted haydi-label-xs">{t('reminderCustomMinutesLabel')}</label>
                      <input
                        type="number"
                        min={0}
                        max={10080}
                        className="haydi-input"
                        value={beforeOffsetCustom}
                        onChange={(e) => setBeforeOffsetCustom(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              )}
              <p className="haydi-muted haydi-hint-xs">{t('todoNotifyHint')}</p>

              <div className="haydi-todo-composer__grid2">
                <div>
                  <label className="haydi-muted haydi-label-xs">{t('todoPriority')}</label>
                  <select
                    className="haydi-select haydi-input"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                  >
                    <option value="none">{t('todoPriority_none')}</option>
                    <option value="low">{t('todoPriority_low')}</option>
                    <option value="med">{t('todoPriority_med')}</option>
                    <option value="high">{t('todoPriority_high')}</option>
                  </select>
                </div>
                <div>
                  <label className="haydi-muted haydi-label-xs">{t('itemPoints')}</label>
                  <input
                    type="number"
                    min={0}
                    max={1000000}
                    className="haydi-input"
                    placeholder="0"
                    value={points}
                    onChange={(e) => setPoints(e.target.value)}
                  />
                </div>
              </div>
              <p className="haydi-muted haydi-hint-xs">{t('itemPointsHint')}</p>

              <div className="haydi-todo-composer__row">
                <button
                  type="button"
                  className="haydi-btn haydi-btn--primary haydi-todo-composer__add"
                  onClick={handleAddTodo}
                  disabled={!newTodo.trim()}
                >
                  {t('add')}
                </button>
              </div>

              <label className="haydi-muted haydi-label-xs">{t('assignTo')}</label>
              <p className="haydi-muted haydi-hint-xs">{t('assignToHint')}</p>
              <select
                multiple
                className="haydi-select haydi-input"
                value={assignedUserIds}
                onChange={(e) => setAssignedUserIds([...e.target.selectedOptions].map((opt) => opt.value))}
                size={4}
              >
                {users.map((u) => (
                  <option value={u._id} key={u._id}>{u.name}</option>
                ))}
              </select>
            </div>

            <ul className="haydi-todo-list">
              {personal.map((todo) => {
                const chip = priorityClass(todo.priority);
                const names = assigneeNames(todo);
                const dl = todoDeadlineState(todo);
                const remLine = formatTodoReminderDisplay(todo, t, i18n);
                const c = normalizeItemColor(todo.color);
                const pts = Number(todo.points || 0);
                const awarded = Number(todo.awardedPoints || 0);
                const canLateDone = !todo.done && isDeadlinePassed(todo);
                const pointsDraft = pointsDrafts[todo._id];
                const pointsValue = pointsDraft !== undefined ? pointsDraft : String(pts);
                const pointsDirty = pointsDraft !== undefined && sanitizePoints(pointsDraft) !== pts;
                return (
                  <li
                    key={todo._id}
                    className={`haydi-todo-card${todo.done ? ' haydi-todo-card--done' : ''}${dl ? ` haydi-todo-card--deadline-${dl}` : ''}`}
                    style={{ '--todo-color': c }}
                  >
                    <input
                      type="checkbox"
                      className="haydi-todo-card__check"
                      checked={!!todo.done}
                      onChange={() => toggleDone(todo)}
                      aria-label={todo.text}
                    />
                    <div className="haydi-todo-card__body">
                      <div className="haydi-todo-card__title-row">
                        <span className="haydi-todo-card__title">{todo.text}</span>
                        {chip && todo.priority && (
                          <span className={chip}>{t(`todoPriority_${todo.priority}`)}</span>
                        )}
                        {pts > 0 && (
                          <span className="haydi-chip">
                            {todo.done
                              ? t('itemPointsAwardedChip', { n: awarded })
                              : t('itemPointsChip', { n: pts })}
                          </span>
                        )}
                        {todo.done && todo.lateDone && (
                          <span className="haydi-chip haydi-chip--med">{t('itemLateDoneChip')}</span>
                        )}
                      </div>
                      {todo.description && (
                        <div className="haydi-todo-card__desc">{todo.description}</div>
                      )}
                      {(todo.deadlineAt?.toDate || (remLine && !todo.done)) && (
                      <div className="haydi-todo-card__meta-grid">
                        {todo.deadlineAt?.toDate && (
                          <div className={`haydi-todo-card__pill${dl === 'overdue' ? ' haydi-todo-card__pill--danger' : ''}${dl === 'soon' ? ' haydi-todo-card__pill--warn' : ''}`}>
                            <span className="haydi-todo-card__pill-label">{t('todoDeadline')}</span>
                            {formatTs(todo.deadlineAt)}
                          </div>
                        )}
                        {remLine && !todo.done && (
                          <div className="haydi-todo-card__pill">
                            <span className="haydi-todo-card__pill-label">{t('todoReminderLabel')}</span>
                            {remLine}
                          </div>
                        )}
                      </div>
                      )}
                      {names && (
                        <div className="haydi-todo-card__assignees">{t('assignTo')}: {names}</div>
                      )}
                      {isAdmin && (
                        <div className="haydi-todo-card__admin-row" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                          <label className="haydi-muted haydi-label-xs" style={{ margin: 0 }}>{t('itemPoints')}</label>
                          <input
                            type="number"
                            min={0}
                            max={1000000}
                            className="haydi-input"
                            style={{ width: 100 }}
                            value={pointsValue}
                            onChange={(e) =>
                              setPointsDrafts((d) => ({ ...d, [todo._id]: e.target.value }))
                            }
                          />
                          {pointsDirty && (
                            <>
                              <button
                                type="button"
                                className="haydi-btn haydi-btn--primary haydi-btn--sm"
                                onClick={() => saveTodoPoints(todo._id)}
                                disabled={!!pointsSaving[todo._id]}
                              >
                                {pointsSaving[todo._id] ? '…' : t('save')}
                              </button>
                              <button
                                type="button"
                                className="haydi-btn haydi-btn--ghost haydi-btn--sm"
                                onClick={() =>
                                  setPointsDrafts((d) => {
                                    const next = { ...d };
                                    delete next[todo._id];
                                    return next;
                                  })
                                }
                              >
                                {t('cancel')}
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {canLateDone && (
                        <button
                          type="button"
                          className="haydi-btn haydi-btn--secondary haydi-btn--sm"
                          onClick={() => markLateDone(todo)}
                          title={t('markLateDoneHint')}
                        >
                          {t('markLateDone')}
                        </button>
                      )}
                      <button
                        type="button"
                        className="haydi-btn haydi-btn--ghost haydi-btn--sm"
                        onClick={() => removeTodo(todo._id)}
                      >
                        {t('delete')}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
            {personal.length === 0 && <p className="haydi-muted" style={{ margin: 0 }}>{t('todoEmptyPersonal')}</p>}
          </div>
        </section>
      </div>
    </div>
  );
}

export default TodoTab;
